import React, { useState, useContext, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import { makeStyles } from "@material-ui/core/styles";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import { Helmet } from "react-helmet";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";
import BRFlag from "../../assets/brazil.png";
import USFlag from "../../assets/unitedstates.png";
import ESFlag from "../../assets/esspain.png";
import ARFlag from "../../assets/arabe.png";
import packageJson from "../../../package.json";
import { getTokens } from "../../styles/tokens";
import { getBackendUrl } from "../../config";
import { useAuthStyles } from "./AuthShell";
import PremiumLoginShell, { getLoginCopy } from "./PremiumLoginShell";

const languageOptions = [
  { value: "pt-BR", label: "Português", icon: BRFlag },
  { value: "en", label: "English", icon: USFlag },
  { value: "es", label: "Spanish", icon: ESFlag },
  { value: "ar", label: "عربي", icon: ARFlag },
];

// Controles abaixo do cartão: versão, idioma e tema.
const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  const control = {
    height: 36,
    boxSizing: "border-box",
    backgroundColor: t.paper,
    border: `1px solid ${t.line}`,
    borderRadius: t.radius.sm,
    color: t.textSecondary,
    transition: `background-color ${t.motion.fast}, border-color ${t.motion.fast}`,
    "&:hover": { backgroundColor: t.muted, borderColor: t.lineStrong },
  };
  return {
    version: { marginRight: 4 },
    langSelector: { position: "relative" },
    langBtn: {
      ...control,
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: "0 12px",
      fontSize: 13,
      fontWeight: 500,
      fontFamily: t.font,
      cursor: "pointer",
      "&:focus-visible": { outline: "none", boxShadow: t.shadow.glow, borderColor: t.brand },
    },
    langOptions: {
      position: "absolute",
      bottom: "calc(100% + 6px)",
      left: "50%",
      transform: "translateX(-50%)",
      minWidth: 160,
      padding: 6,
      zIndex: 1000,
      backgroundColor: t.paper,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.sm,
      boxShadow: t.shadow.lift,
    },
    langOption: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      width: "100%",
      padding: "8px 10px",
      background: "none",
      border: "none",
      borderRadius: t.radius.badge,
      fontSize: 13.5,
      fontWeight: 500,
      fontFamily: t.font,
      color: t.text,
      cursor: "pointer",
      "&:hover": { backgroundColor: t.muted },
      '&[aria-selected="true"]': { backgroundColor: t.tint.soft },
    },
    flagIcon: { width: 20, height: 14, borderRadius: 2 },
    themeToggle: { ...control, width: 36, padding: 0 },
  };
});

const Login = () => {
  const classes = useStyles();
  const auth = useAuthStyles();
  const loginCopy = getLoginCopy();
  const { colorMode } = useContext(ColorModeContext);
  const { appLogoFavicon, appName, mode } = colorMode;
  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [allowSignup, setAllowSignup] = useState(false);
  const { getPublicSetting, getPublicBrand } = useSettings();
  const { handleLogin, loading } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);
  // fundo configurado da marca (o painel é sempre escuro: preferir o do tema escuro)
  const [brandBackground, setBrandBackground] = useState("");

  useEffect(() => {
    const companyId = new URLSearchParams(window.location.search).get("companyId");
    const applyLanguages = (langs) => {
      try {
        const parsed = typeof langs === "string" ? JSON.parse(langs) : langs;
        if (Array.isArray(parsed) && parsed.length) setEnabledLanguages(parsed);
      } catch {}
    };
    const applyBackground = (dark, light) => {
      const file = [dark, light].find((f) => f && String(f).trim());
      setBrandBackground(file ? `${getBackendUrl()}/public/${file}` : "");
    };
    if (companyId) {
      // compatibilidade: ?companyId= força a marca de uma empresa específica
      Promise.all([
        getPublicSetting("appLogoBackgroundDark", companyId),
        getPublicSetting("appLogoBackgroundLight", companyId),
      ])
        .then(([dark, light]) => applyBackground(dark, light))
        .catch(() => {});
      getPublicSetting("userCreation", companyId)
        .then((d) => setAllowSignup(d === "enabled"))
        .catch(() => {});
      getPublicSetting("enabledLanguages", companyId)
        .then(applyLanguages)
        .catch(() => {});
      return;
    }
    // Revenda: marca do domínio acessado
    getPublicBrand()
      .then((brand) => {
        setAllowSignup(brand.userCreation === "enabled");
        applyLanguages(brand.enabledLanguages);
        applyBackground(brand.appLogoBackgroundDark, brand.appLogoBackgroundLight);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fecha o seletor de idioma ao clicar fora
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const handleChangeInput = (e) => setUser({ ...user, [e.target.name]: e.target.value });
  const handlSubmit = (e) => { e.preventDefault(); if (!loading) handleLogin(user); };
  const current = languageOptions.find((o) => o.value === i18n.language) || languageOptions[0];
  const handleSelect = (opt) => {
    i18n.changeLanguage(opt.value);
    localStorage.setItem("language", opt.value);
    setOpen(false);
    window.location.reload();
  };

  const displayName = appName || "Digite AI";
  const below = (
    <>
      <span className={classes.version}>v{packageJson.version}</span>

      {/* Seletor de idioma */}
      <div ref={ref} className={classes.langSelector}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={classes.langBtn}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <img src={current.icon} alt="" className={classes.flagIcon} />
          {current.label} ▾
        </button>
        {open && (
          <div className={classes.langOptions} role="listbox">
            {languageOptions
              .filter((o) => enabledLanguages.includes(o.value))
              .map((opt) => (
                <button
                  type="button"
                  key={opt.value}
                  role="option"
                  aria-selected={opt.value === current.value}
                  onClick={() => handleSelect(opt)}
                  className={classes.langOption}
                >
                  <img src={opt.icon} alt="" className={classes.flagIcon} />
                  {opt.label}
                </button>
              ))}
          </div>
        )}
      </div>

      <IconButton
        className={classes.themeToggle}
        onClick={colorMode.toggleColorMode}
        size="small"
        aria-label={i18n.t("login.toggleTheme", { defaultValue: "Alternar tema claro e escuro" })}
      >
        {mode === "dark" ? (
          <Brightness7Icon fontSize="small" />
        ) : (
          <Brightness4Icon fontSize="small" />
        )}
      </IconButton>
    </>
  );

  return (
    <>
      <Helmet>
        <title>{displayName}</title>
        <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
      </Helmet>
      <CssBaseline />

      <PremiumLoginShell below={below} backgroundImage={brandBackground ? `url("${brandBackground}")` : undefined}>
        <p className={auth.eyebrow}>{loginCopy.eyebrow}</p>
        <h1 className={auth.title}>{loginCopy.welcome[0]}<br />{loginCopy.welcome[1]}</h1>
        <p className={auth.subtitle}>{loginCopy.instruction}</p>

        <form onSubmit={handlSubmit} className={auth.form}>
          <div className={auth.field}>
            <label htmlFor="email" className={auth.label}>
              {loginCopy.email}
            </label>
            <TextField
              variant="outlined"
              required
              fullWidth
              id="email"
              type="email"
              name="email"
              placeholder={loginCopy.emailPlaceholder}
              value={user.email}
              onChange={handleChangeInput}
              autoComplete="email"
              inputProps={{ inputMode: "email", autoCapitalize: "none", spellCheck: false }}
              className={auth.textField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineIcon className={auth.fieldIcon} />
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className={auth.field}>
            <label htmlFor="password" className={auth.label}>
              {i18n.t("login.form.password")}
            </label>
            <TextField
              variant="outlined"
              required
              fullWidth
              name="password"
              placeholder={loginCopy.passwordPlaceholder}
              type={showPassword ? "text" : "password"}
              id="password"
              value={user.password}
              onChange={handleChangeInput}
              autoComplete="current-password"
              className={auth.textField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon className={auth.fieldIcon} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      className={auth.eyeBtn}
                      aria-label={showPassword ? i18n.t("login.hidePassword", { defaultValue: "Ocultar senha" }) : i18n.t("login.showPassword", { defaultValue: "Mostrar senha" })}
                      aria-pressed={showPassword}
                    >
                      {showPassword ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            className={auth.submit}
            disabled={loading}
            aria-busy={loading}
            endIcon={<ArrowForwardIcon />}
          >
            {i18n.t("login.buttons.submit")}
          </Button>

          {allowSignup && (
            <div className="human-login-signup">
              {loginCopy.signup}{" "}
              <Link
                href="#"
                component={RouterLink}
                to="/signup"
                className={auth.link}
              >
                {loginCopy.register}
              </Link>
            </div>
          )}
        </form>
      </PremiumLoginShell>
    </>
  );
};

export default Login;
